import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../store';
import { Logo, Icon, I } from './Shared';
import { ThemeToggle } from './ThemeToggle';
import { isAdminRole, isSuperAdminRole } from '../lib/auth';

/**
 * App-wide left navigation shell: a collapsible rail (icons-only when
 * collapsed) plus a scrollable content area. Replaces the per-page top
 * headers on the hub pages (My Apps, Admin). Focused work surfaces like the
 * Pipeline editor keep their own toolbars and don't use this.
 */
export type ShellNav =
  | 'home' | 'apps' | 'assemblyLines'
  | 'users' | 'settings' | 'enterprises' | 'audit';

const COLLAPSE_KEY = 'af.shell.collapsed';

/* Nav icons not already in the shared library. */
const NAV_ICON = {
  home: <Icon d="M4 11.5L12 4l8 7.5M6 10v10h12V10" />,
  apps: <Icon d="M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z" />,
  assemblyLines: <Icon d="M12 3l9 5-9 5-9-5 9-5zM3 13l9 5 9-5" />,
  enterprises: <Icon d="M4 21V6l8-3 8 3v15M3 21h18M9 21v-5h6v5M8 9h.01M12 9h.01M16 9h.01" />,
};

interface NavItem {
  key: ShellNav;
  label: string;
  path: string;
  icon: React.ReactNode;
}

export function Shell({
  active,
  children,
}: {
  active?: ShellNav;
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  const currentUser = useStore((s) => s.currentUser);
  const currentEnterprise = useStore((s) => s.currentEnterprise);
  const userEnterprises = useStore((s) => s.userEnterprises);
  const allEnterprises = useStore((s) => s.allEnterprises);
  const activeEnterpriseId = useStore((s) => s.activeEnterpriseId);
  const switchEnterprise = useStore((s) => s.switchEnterprise);
  const signOut = useStore((s) => s.signOut);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSE_KEY) === 'true',
  );
  const [wsMenuOpen, setWsMenuOpen] = useState(false);
  const wsRef = useRef<HTMLDivElement>(null);

  // Close the workspace menu on outside click.
  useEffect(() => {
    if (!wsMenuOpen) return;
    const onDoc = (e: MouseEvent): void => {
      if (wsRef.current && !wsRef.current.contains(e.target as Node)) setWsMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [wsMenuOpen]);

  const isAdmin = !!currentUser && isAdminRole(currentUser.role);
  const isSuper = !!currentUser && isSuperAdminRole(currentUser.role);

  const toggle = (): void => {
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem(COLLAPSE_KEY, String(next));
      return next;
    });
  };

  const mainNav: NavItem[] = [
    { key: 'home', label: 'Home', path: '/', icon: NAV_ICON.home },
    { key: 'apps', label: 'My Apps', path: '/apps', icon: NAV_ICON.apps },
    ...(isSuper ? [{ key: 'assemblyLines' as const, label: 'Assembly Lines', path: '/assembly-lines', icon: NAV_ICON.assemblyLines }] : []),
  ];

  const adminNav: NavItem[] = isAdmin
    ? [
        { key: 'users', label: 'Users', path: '/admin/users', icon: I.users },
        { key: 'settings', label: 'Settings', path: '/admin/settings', icon: I.cog },
        ...(isSuper
          ? [
              { key: 'audit' as const, label: 'Audit log', path: '/super-admin/audit-log', icon: I.doc },
            ]
          : []),
      ]
    : [];

  const NavButton = ({ item }: { item: NavItem }): React.ReactElement => (
    <button
      className={`afs-item${active === item.key ? ' afs-item--active' : ''}`}
      onClick={() => navigate(item.path)}
      title={collapsed ? item.label : undefined}
      aria-current={active === item.key ? 'page' : undefined}
    >
      <span className="afs-item-icon">{item.icon}</span>
      <span className="afs-item-label">{item.label}</span>
    </button>
  );

  return (
    <div className={`af afs${collapsed ? ' afs--collapsed' : ''}`}>
      <aside className="afs-rail">
        <div className="afs-rail-top">
          {!collapsed && <Logo />}
          <span style={{ flex: 1 }} />
          <button
            className="afs-toggle"
            onClick={toggle}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? I.chevR : I.chevL}
          </button>
        </div>

        {!collapsed && (currentEnterprise || isSuper || userEnterprises.length > 1) && (() => {
          // The switcher appears when the user can switch: more than one
          // membership, or a super-admin (who may enter any workspace — even
          // before they've picked one, hence this renders with no active
          // workspace too).
          const canSwitch = isSuper || userEnterprises.length > 1;
          if (!canSwitch) {
            return <div className="afs-workspace" title="Current workspace">{currentEnterprise!.name}</div>;
          }
          const memberIds = new Set(userEnterprises.map((m) => m.enterprise.id));
          // Super-admins also see non-member workspaces (from allEnterprises).
          const others = isSuper
            ? allEnterprises.filter((e) => !memberIds.has(e.id))
            : [];
          const pick = (id: string): void => {
            setWsMenuOpen(false);
            if (id === activeEnterpriseId) return;
            // Switching workspace wipes the loaded project (fresh tenant
            // context). Leave any project-scoped page first so its URL param
            // isn't re-fetched against the new workspace — that project lives
            // in the old tenant and would 404. Land on the workspace-scoped
            // apps list instead.
            if (window.location.pathname.startsWith('/pipeline/')) navigate('/apps');
            void switchEnterprise(id);
          };
          return (
            <div className="afs-ws" ref={wsRef}>
              <button
                className="afs-workspace afs-workspace--btn"
                onClick={() => setWsMenuOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={wsMenuOpen}
                title="Switch workspace"
              >
                <span className="afs-ws-name">{currentEnterprise?.name ?? 'Select workspace'}</span>
                <span className={`afs-ws-caret${wsMenuOpen ? ' afs-ws-caret--open' : ''}`}>{I.chevD}</span>
              </button>
              {wsMenuOpen && (
                <div className="afs-ws-menu" role="menu">
                  {userEnterprises.map((m) => (
                    <button
                      key={m.enterprise.id}
                      className={`afs-ws-menu-item${m.enterprise.id === activeEnterpriseId ? ' afs-ws-menu-item--active' : ''}`}
                      role="menuitem"
                      onClick={() => pick(m.enterprise.id)}
                    >
                      <span className="afs-ws-name">{m.enterprise.name}</span>
                      <span className="afs-ws-role">{m.role === 'enterprise_admin' ? 'Admin' : 'Member'}</span>
                    </button>
                  ))}
                  {others.length > 0 && (
                    <>
                      <div className="afs-group-label">All workspaces</div>
                      {others.map((e) => (
                        <button
                          key={e.id}
                          className={`afs-ws-menu-item${e.id === activeEnterpriseId ? ' afs-ws-menu-item--active' : ''}`}
                          role="menuitem"
                          onClick={() => pick(e.id)}
                        >
                          <span className="afs-ws-name">{e.name}</span>
                        </button>
                      ))}
                    </>
                  )}
                  {isSuper && (
                    <button
                      className="afs-ws-menu-item"
                      role="menuitem"
                      onClick={() => { setWsMenuOpen(false); navigate('/super-admin/enterprises'); }}
                    >
                      <span className="afs-item-icon">{NAV_ICON.enterprises}</span>
                      Manage workspaces
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        <nav className="afs-nav" aria-label="Primary">
          {mainNav.map((item) => <NavButton key={item.key} item={item} />)}

          {adminNav.length > 0 && (
            <>
              {!collapsed && <div className="afs-group-label">Admin</div>}
              {collapsed && <div className="afs-divider" />}
              {adminNav.map((item) => <NavButton key={item.key} item={item} />)}
            </>
          )}
        </nav>

        {currentUser && (
          <div className="afs-acct">
            {!collapsed && <div className="afs-acct-email" title={currentUser.email}>{currentUser.email}</div>}
            <ThemeToggle collapsed={collapsed} />
            <button
              className="afs-item"
              onClick={() => void signOut().then(() => navigate('/login'))}
              title={collapsed ? 'Sign out' : undefined}
            >
              <span className="afs-item-icon">
                <Icon d="M14 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2v-2M9 12h12m0 0l-3-3m3 3l-3 3" />
              </span>
              <span className="afs-item-label">Sign out</span>
            </button>
          </div>
        )}
      </aside>

      <div className="afs-main">{children}</div>
    </div>
  );
}
